<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import ProductListItem from 'components/User/Order/ProductListItem.vue';
import OrderService from 'src/core/services/OrderService';
import { useQuasar } from 'quasar';
import { OrdersStatuses, RefundCancellationTypes } from 'src/core/constants/Orders';
import { useRouter } from 'vue-router';
import { ProductWebItem } from 'src/core/types/Products';

export default defineComponent({
  name: 'CancelAndRefundDialog',
  computed: {
    OrdersStatuses() {
      return OrdersStatuses;
    },
    RefundCancellationTypes() {
      return RefundCancellationTypes;
    },
  },
  components: { ProductListItem },

  props: {
    modelValue: Boolean,
    order: Object,
    type: Number,
  },

  setup(props) {
    const $q = useQuasar();
    const $router = useRouter();
    const caseList = ref({
      data: [],
      loading: false,
    });

    if (caseList.value.data.length === 0) {
      caseList.value.loading = true;
      OrderService.orderCaseList()
        .then(({ status, data }) => {
          if (status._200_) {
            caseList.value.data = data;
          }
          caseList.value.loading = false;
        })
        .catch(() => {
          caseList.value.loading = false;
        });
    }

    const requestForm = ref({
      orderId: null,
      text: '',
      variationCodes: [],
      note: '',
    });

    const requestLoading = ref(false);
    const requestType = ref(null);

    const selectedProducts = computed(() => {
      return props.order.products.filter((product) => requestForm.value.variationCodes.includes(product.variations[0].code));
    });

    return {
      requestForm,
      requestLoading,
      requestType,
      caseList,
      selectedProducts,
      refundOrCancellation() {
        if (requestForm.value.variationCodes.length === 0 && props.type === RefundCancellationTypes.ORDER_REFUND) {
          $q.notify({
            color: 'red',
            icon: 'fa-solid fa-circle-exclamation',
            message: 'İptal talebi için lütfen ürün seçin',
          });
          return;
        }

        const requestFormParams = {};
        let requestFn = null;
        switch (props.type) {
          case RefundCancellationTypes.ORDER_REFUND:
            requestFormParams.orderId = props.order?.id || null;
            requestFormParams.text = requestForm.value.text;
            requestFormParams.variationCodes = requestForm.value.variationCodes;
            requestFormParams.note = requestForm.value.note;
            requestFn = OrderService.requestOrderRefund;
            break;
          case RefundCancellationTypes.ORDER_CANCELLATION:
            requestFormParams.orderId = props.order?.id || null;
            requestFormParams.text = requestForm.value.text;
            requestFormParams.note = requestForm.value.note;
            requestFn = OrderService.requestOrderCancellation;
            break;
        }

        if (!requestFn) return;

        requestLoading.value = true;
        requestFn({ ...requestFormParams })
          .then(({ status, message }) => {
            if (status._200_) {
              $q.notify({
                color: 'green',
                icon: 'fa-regular fa-circle-check',
                message: 'Talebiniz oluşturulmuştur, en kısa sürede işleme alınıp tarafınıza bilgi verilecektir',
              });
              $router.push({ name: 'cancellation-or-refund' });
            } else {
              $q.notify({
                color: 'red',
                icon: 'fa-solid fa-circle-exclamation',
                message,
              });
            }
            requestLoading.value = false;
          })
          .catch(() => {
            requestLoading.value = false;
          });
      },
    };
  },

  methods: {
    selectProductItem(selectList: ProductWebItem[]) {
      this.requestForm.variationCodes = selectList.map((o) => o.variations[0].code);
    },
  },
});
</script>

<template>
  <q-dialog :model-value="modelValue" @update:model-value="$emit('update:model-value', $event)">
    <q-card style="min-width: 400px">
      <q-card-section class="flex justify-between items-center bg-red-5 text-white">
        <div class="text-body1 text-weight-bold flex items-center">
          <q-icon name="help" size="1.7rem" left />
          İptal/İade Talebi Oluştur
        </div>
        <div>
          <q-btn flat round icon="close" @click="$emit('update:model-value', false)" />
        </div>
      </q-card-section>
      <q-card-section>
        <q-form>
          <div class="row">
            <div class="col-12">
              <div class="flex justify-between q-mb-md">
                <div>
                  <div class="text-body1 text-weight-bold q-mb-xs">Ürün Seçin</div>
                  <div class="text-body2 text-grey-8">İade yapmak istediğiniz ürünleri seçin</div>
                </div>
                <div v-if="OrdersStatuses.ORDER_REFUND === type">
                  <q-btn color="primary" class="text-weight-bold" label="Tümünü Seç" :loading="caseList.loading" no-caps @click="selectProductItem(order.products)" />
                </div>
              </div>
              <div v-if="OrdersStatuses.ORDER_REFUND === type" class="q-mb-lg bg-grey-3 q-py-sm">
                <q-list>
                  <product-list-item :list="order.products" :selected="selectedProducts" selectable @select="selectProductItem" />
                </q-list>
              </div>
              <div class="user-support--input">
                <q-select label="İade nedenini seçin" v-model="requestForm.text" :options="caseList.data" :loading="caseList.loading" outlined>
                  <template v-slot:prepend>
                    <q-icon name="arrow_forward" />
                  </template>
                </q-select>
              </div>
              <div class="user-support--input">
                <q-input v-model="requestForm.note" outlined label="İade Mesajı" type="textarea" rows="5">
                  <template v-slot:prepend>
                    <q-icon name="arrow_forward" />
                  </template>
                </q-input>
              </div>
              <div class="user-support--input column items-stretch self-end">
                <q-btn color="primary" class="text-weight-bold" label="Talep Oluştur" :loading="requestLoading" no-caps @click="refundOrCancellation" />
              </div>
            </div>
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.user-support--input {
  margin-bottom: 1rem;
}
</style>
